// pages/InstructorSignUp.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/SetNavbar";
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Grid,
  Alert,
  Box,
  Typography,
  Container,
  Paper,
  Chip,
  Fade,
  Stack,
  Divider,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Person,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  WorkspacePremium,
  ArrowForward,
  Verified,
  Star,
  CheckCircle,
} from "@mui/icons-material";
import { postData } from "../../services/Request";
import CircularProgress from "@mui/material/CircularProgress";
import S1 from "../../assets/ContactUs/S1.png";
import "../../styles/SignUp.css";

export default function InstructorSignUp() {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [alertMessage, setAlertMessage] = useState("");
  const [severity, setSeverity] = useState("");
  const [loader, setLoader] = useState(false);
  const [agree, setAgree] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form field states for validation
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Check if user is already logged in
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        if (data && data.usertype === "Instructor") {
          nav("/instructor/dashboard");
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, [nav]);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "firstname":
        if (!value.trim()) error = "First name is required";
        break;
      case "lastname":
        if (!value.trim()) error = "Last name is required";
        break;
      case "email":
        if (!value.trim()) {
          error = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "password":
        if (!value) {
          error = "Password is required";
        } else if (value.length < 6) {
          error = "Password must be at least 6 characters";
        }
        break;
      case "confirm_password":
        if (!value) {
          error = "Please confirm your password";
        } else if (value !== formData.password) {
          error = "Passwords do not match";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setAlertMessage("Please fix all errors before submitting");
      setSeverity("warning");
      return;
    }

    if (!agree) {
      setAlertMessage("Please agree to the Terms & Conditions");
      setSeverity("warning");
      return;
    }

    setLoader(true);
    setAlertMessage("");

    const details = {
      firstname: formData.firstname,
      lastname: formData.lastname,
      email: formData.email,
      password: formData.password,
      confirm_password: formData.confirm_password,
    };

    postData("instuctorsignup", details)
      .then((response) => {
        setLoader(false);
        if (response.status === true) {
          setAlertMessage("✅ " + response.message);
          setSeverity("success");
          setTimeout(() => {
            nav("/teacherlogin");
          }, 1000);
        } else {
          setAlertMessage("❌ " + response.message);
          setSeverity("error");
        }
      })
      .catch((error) => {
        setLoader(false);
        if (error.request?.status === 422) {
          setAlertMessage(
            "❌ " + error.response?.data?.errors?.email || "Validation error",
          );
          setSeverity("error");
        } else if (error.request?.status === 500) {
          setAlertMessage(
            "❌ Connection to internet lost, please check connection and retry",
          );
          setSeverity("error");
        } else {
          setAlertMessage("❌ Error creating account");
          setSeverity("error");
        }
      });
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleAgreeChange = (event) => {
    setAgree(event.target.checked);
  };

  return (
    <>
      <Header />
      <Box
        className="instructor-signup-container"
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(180deg, #F7F8F2 0%, #e8efe6 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          py: { xs: 4, md: 0 },
        }}
      >
        {/* Animated Background Decorations */}
        <Box
          sx={{
            position: "absolute",
            top: -200,
            right: -200,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255, 107, 107, 0.04) 0%, transparent 70%)",
            zIndex: 0,
            animation: "pulse 8s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -200,
            left: -200,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255, 217, 61, 0.03) 0%, transparent 70%)",
            zIndex: 0,
            animation: "pulse 8s ease-in-out infinite 2s",
          }}
        />

        {/* Floating Particles */}
        {[...Array(4)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: `${10 + i * 25}%`,
              left: `${5 + (i % 3) * 30}%`,
              width: `${4 + i * 2}px`,
              height: `${4 + i * 2}px`,
              borderRadius: "50%",
              backgroundColor:
                i % 2 === 0
                  ? "rgba(255, 107, 107, 0.12)"
                  : "rgba(255, 217, 61, 0.12)",
              animation: `float ${6 + i * 2}s ease-in-out infinite ${i * 1.5}s`,
              zIndex: 0,
            }}
          />
        ))}

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(43, 173, 101, 0.06)",
              maxWidth: 1100,
              mx: "auto",
            }}
          >
            <Grid container>
              {/* Left Side - Image & Branding */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: { xs: "none", md: "flex" },
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                  background:
                    "linear-gradient(135deg, rgba(255, 107, 107, 0.04), rgba(255, 217, 61, 0.02))",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Image */}
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 400,
                  }}
                >
                  <Box
                    component="img"
                    src={S1}
                    alt="Instructor Sign Up"
                    sx={{
                      width: "100%",
                      height: "auto",
                      borderRadius: 3,
                      border: "1px solid rgba(43, 173, 101, 0.06)",
                      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.06)",
                      transition: "transform 0.5s ease",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                    }}
                  />

                  {/* Floating Badge - Instructor Specific */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 20,
                      left: 20,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 107, 107, 0.15)",
                      borderRadius: 2,
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "rgba(255, 107, 107, 0.1)",
                        borderRadius: "50%",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <WorkspacePremium sx={{ color: "#FF6B6B" }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                      >
                        Join 500+
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#1a1a1a", fontWeight: 700 }}
                      >
                        Expert Instructors
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Trust Badges - Instructor Specific */}
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mt: 4, flexWrap: "wrap", justifyContent: "center" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Verified sx={{ color: "#FF6B6B", fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                    >
                      Verified Instructors
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Star sx={{ color: "#FFD93D", fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                    >
                      4.9/5 Rating
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CheckCircle sx={{ color: "#FF6B6B", fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                    >
                      Earn Money
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* Right Side - Sign Up Form */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                  {/* Header - Instructor Specific */}
                  <Box sx={{ mb: 3 }}>
                    <Chip
                      label="👨‍🏫 Instructor Sign Up"
                      sx={{
                        backgroundColor: "rgba(255, 107, 107, 0.1)",
                        color: "#FF6B6B",
                        fontWeight: 600,
                        mb: 2,
                        border: "1px solid rgba(255, 107, 107, 0.15)",
                      }}
                    />
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        fontSize: { xs: "1.8rem", md: "2.2rem" },
                      }}
                    >
                      Start Teaching Today!
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(26, 26, 26, 0.5)",
                        mt: 0.5,
                      }}
                    >
                      Join our platform as an instructor and share your
                      expertise with thousands of eager learners worldwide.
                    </Typography>
                  </Box>

                  {/* Alert Message */}
                  {alertMessage && (
                    <Fade in timeout={300}>
                      <Alert
                        variant="filled"
                        severity={severity}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          backgroundColor:
                            severity === "success" ? "#2BAD65" : "#FF6B6B",
                          "& .MuiAlert-icon": {
                            color: "#ffffff",
                          },
                        }}
                      >
                        {alertMessage}
                      </Alert>
                    </Fade>
                  )}

                  {/* Form */}
                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="firstname"
                          label="First Name"
                          value={formData.firstname}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.firstname}
                          helperText={errors.firstname}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person sx={{ color: "rgba(26,26,26,0.2)" }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(43, 173, 101, 0.04)",
                              color: "#1a1a1a",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(43, 173, 101, 0.08)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(43, 173, 101, 0.15)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6B6B",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(26, 26, 26, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          name="lastname"
                          label="Last Name"
                          value={formData.lastname}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.lastname}
                          helperText={errors.lastname}
                          InputProps={{
                            startAdornment: (
                              <Person sx={{ color: "rgba(26,26,26,0.2)" }} />
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(43, 173, 101, 0.04)",
                              color: "#1a1a1a",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(43, 173, 101, 0.08)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(43, 173, 101, 0.15)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6B6B",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(26, 26, 26, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="email"
                          label="Email Address"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.email}
                          helperText={errors.email}
                          InputProps={{
                            startAdornment: (
                              <Email sx={{ color: "rgba(26,26,26,0.2)" }} />
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(43, 173, 101, 0.04)",
                              color: "#1a1a1a",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(43, 173, 101, 0.08)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(43, 173, 101, 0.15)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6B6B",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(26, 26, 26, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="password"
                          label="Password"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.password}
                          helperText={errors.password}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock sx={{ color: "rgba(26,26,26,0.2)" }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleTogglePassword}
                                  edge="end"
                                  sx={{ color: "rgba(26,26,26,0.2)" }}
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(43, 173, 101, 0.04)",
                              color: "#1a1a1a",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(43, 173, 101, 0.08)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(43, 173, 101, 0.15)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6B6B",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(26, 26, 26, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="confirm_password"
                          label="Confirm Password"
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirm_password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.confirm_password}
                          helperText={errors.confirm_password}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock sx={{ color: "rgba(26,26,26,0.2)" }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleToggleConfirmPassword}
                                  edge="end"
                                  sx={{ color: "rgba(26,26,26,0.2)" }}
                                >
                                  {showConfirmPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(43, 173, 101, 0.04)",
                              color: "#1a1a1a",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "rgba(43, 173, 101, 0.08)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(43, 173, 101, 0.15)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6B6B",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(26, 26, 26, 0.4)",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agree}
                          onChange={handleAgreeChange}
                          sx={{
                            color: "rgba(26,26,26,0.2)",
                            "&.Mui-checked": {
                              color: "#FF6B6B",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ color: "rgba(26,26,26,0.3)" }}
                        >
                          I agree to the{" "}
                          <Link
                            href="#"
                            sx={{
                              color: "#FF6B6B",
                              textDecoration: "none",
                              "&:hover": {
                                color: "#FF8E53",
                              },
                            }}
                          >
                            Terms & Conditions
                          </Link>
                        </Typography>
                      }
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loader || !agree}
                      startIcon={!loader ? <ArrowForward /> : null}
                      sx={{
                        mt: 2,
                        mb: 2,
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: 700,
                        background: "linear-gradient(135deg, #FF6B6B, #FF8E53)",
                        borderRadius: 2,
                        color: "#ffffff",
                        boxShadow: "0 4px 25px rgba(255, 107, 107, 0.3)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 35px rgba(255, 107, 107, 0.4)",
                          background:
                            "linear-gradient(135deg, #FF8E53, #FF6B6B)",
                        },
                        "&:disabled": {
                          backgroundColor: "rgba(255, 107, 107, 0.15)",
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      {loader ? (
                        <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                      ) : (
                        "Create Account"
                      )}
                    </Button>

                    {/* Divider */}
                    <Box sx={{ my: 2 }}>
                      <Divider
                        sx={{ backgroundColor: "rgba(43, 173, 101, 0.08)" }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26,26,26,0.2)", px: 2 }}
                        >
                          Or continue with
                        </Typography>
                      </Divider>
                    </Box>

                    {/* Login Link */}
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(26,26,26,0.3)" }}
                      >
                        Already have an instructor account?{" "}
                        <Link
                          href="/teacherlogin"
                          sx={{
                            color: "#FF6B6B",
                            fontWeight: 600,
                            textDecoration: "none",
                            "&:hover": {
                              color: "#FF8E53",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Login
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>

        <style>
          {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.1); opacity: 0.6; }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }

          .instructor-signup-container {
            min-height: 100vh;
          }
        `}
        </style>
      </Box>
    </>
  );
}
